import React, { useEffect, useState } from "react"
import MetaTags from "react-meta-tags"
import { Container, Row, Col, Card, CardBody, Spinner } from "reactstrap"
import axios from "axios"
import { getCurrentUser } from "../../helpers/Utils"

/** Set a YouTube video ID here when the overview is ready. Leave empty to show coming-soon. */
const CALLMAN_YOUTUBE_VIDEO_ID = ""

const TEAM_FEATURES = [
  "Synced with klikCRM.",
  "Add/update leads from android app only.",
  "Push notifications & call reminder (priority reminders) as well.",
  "Online/offline access.",
]

const ADMIN_REPORTS = [
  "Call volume monitoring.",
  "Call details monitoring.",
  "Call status monitoring.",
  "Call reporting on EOD (upcoming).",
]

const UPCOMING_ADDONS = [
  "AutoDialer",
  "Call recording",
  "Speech to text transcription",
  "Summaries lead history.",
  "Staff/customer tracking via keywords (text in speech).",
  "Calling AI",
  "Lead scoring via AI.",
  "Overlay screen for lead info like Truecaller.",
  "Kiosk settings (via Google workSpace).. to permit (enable/disable) for using other applications.",
  "Client & field manager’s login.",
]

const isAdminUser = () => {
  const user = getCurrentUser()
  if (!user) return false
  return (
    user.role_name === "Administrator" ||
    user.role_name === "Sub Administrator" ||
    user.role_id == 1
  )
}

const formatVersionLabel = name => {
  const value = String(name || "").trim()
  if (!value) return ""
  return /^v/i.test(value) ? value : `v${value}`
}

const CompactApkCard = ({ version: v }) => (
  <div>
    {v.feature_image_url ? (
      <img
        src={v.feature_image_url}
        alt={`CallMan ${v.version_name}`}
        className="mb-2"
        style={{
          width: "100%",
          height: 80,
          objectFit: "cover",
          borderRadius: 6,
        }}
      />
    ) : null}
    <div className="text-muted mb-2" style={{ fontSize: "0.8em" }}>
      Code {v.version_code}
      {v.updated_at ? ` · ${v.updated_at}` : ""}
    </div>
    {v.message ? (
      <div
        className="mb-3"
        style={{
          whiteSpace: "pre-wrap",
          fontSize: "0.85em",
          lineHeight: 1.4,
        }}
      >
        {v.message}
      </div>
    ) : (
      <div className="text-muted mb-3" style={{ fontSize: "0.85em" }}>
        Latest build is ready to download.
      </div>
    )}
    {v.apk_url ? (
      <a
        className="btn btn-sm btn-outline-primary"
        href={v.apk_url}
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        Download
      </a>
    ) : null}
  </div>
)

const ApkAccordion = ({ versions }) => {
  const latest = versions.slice(0, 3)
  const [openId, setOpenId] = useState(null)

  return (
    <div>
      <div className="fw-semibold mb-2">Latest 3 APK builds</div>
      {latest.length === 0 ? (
        <div className="alert alert-warning mb-0 py-2">
          No CallMan APK uploaded yet.
        </div>
      ) : (
        <div className="accordion">
          {latest.map(v => {
            const isOpen = openId === v.id
            return (
              <div className="accordion-item" key={v.id}>
                <h2 className="accordion-header">
                  <button
                    type="button"
                    className={`accordion-button${isOpen ? "" : " collapsed"}`}
                    onClick={() => setOpenId(isOpen ? null : v.id)}
                    style={{ fontSize: "0.92em", padding: "10px 14px" }}
                  >
                    <span className="d-flex align-items-center flex-wrap w-100 pe-2" style={{ gap: 8 }}>
                      <strong>{formatVersionLabel(v.version_name)}</strong>
                      {v.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Older</span>
                      )}
                    </span>
                  </button>
                </h2>
                <div className={`accordion-collapse collapse${isOpen ? " show" : ""}`}>
                  <div className="accordion-body py-3">
                    <CompactApkCard version={v} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const UpcomingAddonsSection = () => (
  <div className="mt-4 pt-3" style={{ borderTop: "1px solid #eff2f7" }}>
    <h5 className="mb-3">A few upcoming addons (optional)</h5>
    <Row>
      {UPCOMING_ADDONS.map((item, i) => (
        <Col md={6} key={i} className="mb-1">
          <div className="d-flex align-items-start">
            <i
              className="fas fa-angle-right me-2 mt-1 text-muted"
              style={{ fontSize: "0.8em" }}
            />
            <span>{item}</span>
          </div>
        </Col>
      ))}
    </Row>
  </div>
)

const CallmanAppPage = () => {
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [versions, setVersions] = useState([])
  const [error, setError] = useState("")
  const isAdmin = isAdminUser()

  useEffect(() => {
    const user = getCurrentUser()
    if (!user?.token) {
      setLoading(false)
      return
    }

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
          "company_name"
        )}/callman-apk-latest`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      )
      .then(res => {
        if (!res.data?.status) {
          setError(res.data?.message || "Unable to load CallMan APK")
          return
        }
        setEnabled(!!res.data.callman_enabled)
        setVersions(Array.isArray(res.data.versions) ? res.data.versions : [])
      })
      .catch(() => setError("Failed to load CallMan APK details"))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="page-content">
      <MetaTags>
        <title>CallMan App | KlikCRM</title>
      </MetaTags>
      <Container fluid>
        <Card className="mb-4 border-0 shadow-sm overflow-hidden">
          <div
            className="d-flex align-items-center justify-content-between flex-wrap"
            style={{
              padding: "18px 20px",
              background: "linear-gradient(90deg, #f3fbfb 0%, #ffffff 70%)",
              borderBottom: "1px solid #e6eef0",
              gap: 12,
            }}
          >
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 10,
                  background: "#159197",
                  flexShrink: 0,
                }}
              >
                <i
                  className="fab fa-android"
                  style={{ color: "#fff", fontSize: 22 }}
                />
              </div>
              <div>
                <h4 className="mb-0" style={{ fontWeight: 700, fontSize: 20 }}>
                  CallMan App
                </h4>
                <div className="text-muted" style={{ fontSize: "0.88em" }}>
                  {enabled
                    ? "Explore CallMan features and download the latest builds."
                    : "Try CallMan — explore features and download the APK."}
                </div>
              </div>
            </div>
            {!loading && !error ? (
              <span
                className={`badge ${enabled ? "bg-success" : "bg-info"}`}
                style={{ fontSize: "0.75em", padding: "6px 10px" }}
              >
                {enabled ? "Enabled for your team" : "Available to try"}
              </span>
            ) : null}
          </div>

          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : error ? (
              <div className="alert alert-danger mb-0">{error}</div>
            ) : (
              <>
                <Row>
                  <Col lg={7} className="mb-3 mb-lg-0">
                    <h5 className="mb-3">Features in basic package</h5>

                    {isAdmin ? (
                      <div className="mb-3">
                        <div className="fw-semibold mb-2">Reports</div>
                        <ul className="ps-3 mb-0">
                          {ADMIN_REPORTS.map((item, i) => (
                            <li key={i} className="mb-1">
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    <div className="mb-3">
                      {isAdmin ? (
                        <div className="fw-semibold mb-2">For team</div>
                      ) : null}
                      <ul className="ps-3 mb-0">
                        {TEAM_FEATURES.map((item, i) => (
                          <li key={i} className="mb-1">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <ApkAccordion versions={versions} />
                  </Col>

                  <Col lg={5}>
                    <div className="fw-semibold mb-2">Watch overview</div>
                    {CALLMAN_YOUTUBE_VIDEO_ID ? (
                      <div
                        style={{
                          position: "relative",
                          paddingBottom: "56.25%",
                          height: 0,
                          overflow: "hidden",
                          borderRadius: 8,
                          background: "#000",
                        }}
                      >
                        <iframe
                          title="CallMan App overview"
                          src={`https://www.youtube.com/embed/${CALLMAN_YOUTUBE_VIDEO_ID}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: 0,
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center text-center"
                        style={{
                          position: "relative",
                          paddingBottom: "56.25%",
                          height: 0,
                          borderRadius: 8,
                          background:
                            "linear-gradient(145deg, #0f3d42 0%, #1a6b73 55%, #159197 100%)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 24,
                          }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center mb-3"
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              background: "rgba(255,255,255,0.16)",
                              border: "2px solid rgba(255,255,255,0.55)",
                            }}
                          >
                            <i
                              className="fas fa-play"
                              style={{
                                color: "#fff",
                                fontSize: 22,
                                marginLeft: 3,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 18,
                              letterSpacing: 0.2,
                            }}
                          >
                            Overview video coming soon
                          </div>
                          <div
                            style={{
                              color: "rgba(255,255,255,0.82)",
                              fontSize: 13,
                              marginTop: 6,
                              maxWidth: 280,
                            }}
                          >
                            A short walkthrough of CallMan will be available
                            here shortly.
                          </div>
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>

                {isAdmin ? <UpcomingAddonsSection /> : null}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default CallmanAppPage
